import React, { useState } from "react";
import { Dialog, Box, Snackbar, Alert, Slide, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useQueryClient } from '@tanstack/react-query';
import ManualInvoiceForm from '../../../components/UploadInvoicesAI/ManualInvoiceForm';
import useFetchWithMsal from "../../../hooks/useFetchWithMsal";
import { protectedResources } from "../../../authConfig";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function ManualInvoiceDialog({ open, onClose }) {
  const queryClient = useQueryClient(); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const handleSubmit = async (formData, pdfFile) => {
    setIsSubmitting(true);
    try {
      let fileData = null;
      if (pdfFile) {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(pdfFile);
        });
        
        const base64String = await base64Promise;
        fileData = {
          name: pdfFile.name,
          type: pdfFile.type,
          chunks: [base64String.split(',')[1]]
        };
      }

      const response = await execute(
        'POST',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/manual`,
        {
          invoiceData: {
            supplierNumber: formData.supplierNumber,
            supplierName: formData.supplierName,
            companyName: formData.companyName,
            companyId: formData.companyId,
            invoiceNumber: formData.invoiceNumber,
            invoiceDate: formData.invoiceDate,
            allocationNumber: formData.allocationNumber,
            currencyCode: formData.currencyCode,
            totalAmount: formData.total,
            subtotal: formData.subtotal,
            vatAmount: formData.vatAmount,
            items: formData.items
          },
          fileData
        }
      );

      queryClient.invalidateQueries(['spInvoices']);

      window.dispatchEvent(new Event('invoice-status-changed'));

      if (response.status === 'REQUIRES_ATTENTION') {
        setSnackbar({
          open: true,
          message: response.message || response.errorDetails?.hebrewMessage || 'החשבונית דורשת תשומת לב',
          severity: 'warning'
        });
        
        setTimeout(() => {
          onClose();
        }, 2500);
        return;
      }

      if (response.status === 'WAITING_FOR_GOODS_RECEIPT') {
        setSnackbar({
          open: true,
          message: response.message || 'החשבונית ממתינה לקבלת סחורה',
          severity: 'info'
        });
        
        setTimeout(() => {
          onClose();
        }, 2500);
        return;
      }

      setSnackbar({
        open: true,
        message: 'חשבונית נשמרה ועובדה בהצלחה!',
        severity: 'success'
      });

      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error submitting manual invoice:', error);
      
      queryClient.invalidateQueries(['spInvoices']);
      window.dispatchEvent(new Event('invoice-status-changed'));
      const errorMessage = error.data?.message || 
                          error.data?.errorDetails?.hebrewMessage ||
                          error.response?.data?.message || 
                          error.response?.data?.errorDetails?.hebrewMessage ||
                          error.msg ||
                          'שגיאה בשמירת החשבונית';
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
            width: '95%',
            maxWidth: '1200px'
          }
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            left: 8,
            top: 8,
            color: '#666',
            zIndex: 1,
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ 
          overflow: 'auto',
          p: 3
        }}>
          <ManualInvoiceForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            isSubmitting={isSubmitting}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ManualInvoiceDialog;